import * as React from "react";
import {
  Link,
  createFileRoute,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { LockIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import type { Role, RoleFormData } from "@/lib/queries/roles";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { roleFormSchema, rolesListQueryOptions } from "@/lib/queries/roles";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { generateDefaultValue } from "@/lib/utils/zod";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
});

export const Route = createFileRoute("/_app/roles/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(rolesListQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "roles",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 400,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 400,
  },
  {
    id: "actions",
    header: "Actions",
    maxSize: 280,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("roles", ["update"]);
      const canDelete = useResourceAccessPermission("roles", ["delete"]);
      const canManage = useResourceAccessPermission("permissions", ["manage"]);

      return (
        <Actions>
          {canManage.data && (
            <Button
              size="lg"
              variant="ghost"
              className="w-full justify-start text-base"
              asChild
            >
              <Link to="/roles/$id/permissions" params={{ id: original.id }}>
                <LockIcon className="size-5" />
                Permissions
              </Link>
            </Button>
          )}
          {canEdit.data && (
            <EditDialog asChild role={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog role={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<Role>>;

function RouteComponent() {
  const searchParams = Route.useSearch();

  const roles = useQuery(rolesListQueryOptions(searchParams));

  const canEdit = useResourceAccessPermission("roles", ["update"]);
  const canDelete = useResourceAccessPermission("roles", ["delete"]);
  const canManage = useResourceAccessPermission("permissions", ["manage"]);
  const canCreate = useResourceAccessPermission("roles", ["create"]);

  const showActions = Boolean(canEdit.data || canDelete.data || canManage.data);

  const table = useReactTable({
    data: roles.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: roles.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Roles</h1>
        {canCreate.data && (
          <CreateDialog asChild>
            <Button>
              <PlusIcon />
              Create Role
            </Button>
          </CreateDialog>
        )}
      </div>

      {/* <div className="mb-4">
        <Input
          placeholder="Search all columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
      </div> */}

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={roles.isLoading} />
      </div>
      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function DeleteDialog({ role }: { role: Role }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn(id: number) {
      return api.raw.delete(`api/v1/roles/${id}`);
    },
    onSuccess(_, id) {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] });
      queryClient.removeQueries({
        queryKey: ["roles", "detail", id],
      });
      setOpen(false);
      toast.success(`Role "${role.name}" deleted successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Role</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the role "{role.name}"? This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(role.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  role,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; role: Role }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    async mutationFn({ id, ...data }: Role) {
      return api.raw.put(`api/v1/roles/${id}`, { json: data }).json<Role>();
    },
    onSuccess(data) {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] });
      queryClient.setQueryData(["roles", "detail", data.id], data);

      setOpen(false);
      toast.success(`Role "${role.name}" updated successfully`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Role</DialogTitle>
          <RoleForm
            defaultValue={role}
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Update"}
            onSubmit={(data) => mutation.mutate({ id: role.id, ...data })}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: RoleFormData) => {
      return api.raw.post("api/v1/roles/", { json: data }).json<Role>();
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] });

      setOpen(false);
      toast.success(`Role "${role.name}" created successfully`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Role</DialogTitle>
          <RoleForm
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Create"}
            onSubmit={mutation.mutate}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function RoleForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: RoleFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: RoleFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      ...generateDefaultValue(roleFormSchema),
      ...defaultValue,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter role name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Enter role description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}
